import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { matchPath, useLocation } from 'react-router';
import { alpha, useTheme } from '@mui/material/styles';
import { VolumeOffRounded, VolumeUpRounded } from '@mui/icons-material';

import { INLINE_PLAYER_INSTANCE_KEY } from '@/modules/common/components/Player/constants';
import { NEXT_CLIP_COMPLETE_EVENT } from '@/modules/common/components/Player/constants/events';
import { CHANNEL_LIST_PATH, DEFAULT_VIEW_PATH } from '@/modules/common/constants/routePath';
import { ALPHA_700 } from '@/mui/constants/opacity';

import { usePlayerWidget } from '@/modules/common/components/Player/helpers';
import { useOnScreen } from '@/modules/common/helpers/customHooks';
import { getPathNameByParams } from '@/modules/common/helpers/history';

import Player from '../../../../common/components/Player/Player';
import { Box, BrowserLink, Button } from '@/mui/components';

import styles from './VideoPlayer.module.scss';

function Video(props) {
  const theme = useTheme();
  const location = useLocation();
  const LWP = usePlayerWidget(INLINE_PLAYER_INSTANCE_KEY);
  const videoRef = useRef(null);
  const inView = useOnScreen(videoRef, true);
  const [dimensions, setDimensions] = useState({
    width: null,
    height: null,
    top: 0,
    left: 0,
  });
  const [isMuted, setMuteState] = useState(true);

  const color = theme.palette.background.default;

  const gradient = (
    <Box
      data-a="player-gradient"
      to={getPathNameByParams(DEFAULT_VIEW_PATH, {
        channel: props.channel.id,
        video: props.video.mediaid,
      })}
      component={BrowserLink}
      className={styles.Gradient}
      style={{
        background: `linear-gradient(90deg,${color} 0,${alpha(color, ALPHA_700)} 294px,${alpha(color, 0)} 460px),linear-gradient(360deg,${color} 0,${alpha(color, 0)} 298px)`,
      }}
    />
  );

  const VolumeIcon = isMuted ? VolumeOffRounded : VolumeUpRounded;

  useEffect(() => {
    const fn = () => {
      if (videoRef.current) {
        const { clientWidth, clientHeight } = videoRef.current;

        let width = clientHeight * (16 / 9);
        let height = clientHeight;
        let top = 0;
        let left = (width - clientWidth) / 2;

        if (clientWidth > width) {
          width = clientWidth;
          height = width / (16 / 9);

          top = (height - width) / 2;
          left = 0;
        }

        setDimensions({
          width,
          height,
          top,
          left,
        });
      }
    };

    fn();

    window.addEventListener('resize', fn);

    return () => {
      window.removeEventListener('resize', fn);
      LWP.muted(true);
    };
  }, []);

  useEffect(() => {
    if (!matchPath(CHANNEL_LIST_PATH, location.pathname)) {
      setMuteState(true);
      LWP.muted(true);
      LWP.pause();
    } else {
      LWP.play();
    }
  }, [props.video.mediaid, location.pathname]);

  return (
    <div className={styles.Wrapper} ref={videoRef}>
      {inView && (
        <>
          <Player
            instanceKey={INLINE_PLAYER_INSTANCE_KEY}
            video={props.video}
            playerPropsPassThrow={props.channel.playerPropsPassThrow}
            clipSource={props.channel.playlistId}
            listeners={{
              [NEXT_CLIP_COMPLETE_EVENT]: () => props.onEnded(),
            }}
            className={styles.Player}
            style={{
              width: dimensions.width,
              height: dimensions.height,
              transform: `translate3d(-${dimensions.left}px, -${dimensions.top}px, 0)`,
            }}
          />
          {gradient}
          <div className={styles.VolumeButton}>
            <Button
              data-a="player-button"
              aria-label="volume"
              type="button"
              variant="outlined"
              color="inherit"
              size="large"
              shape="circular"
              square
              onClick={(event) => {
                event.preventDefault();

                setMuteState((prev) => {
                  const muted = !prev;
                  LWP.muted(muted);

                  return muted;
                });
              }}
              startIcon={<VolumeIcon fontSize="large" />}
            />
          </div>
        </>
      )}
    </div>
  );
}

Video.propTypes = {
  video: PropTypes.shape({
    mediaid: PropTypes.string,
  }).isRequired,
  channel: PropTypes.shape({
    id: PropTypes.number.isRequired,
    playlistId: PropTypes.number.isRequired,
    playerPropsPassThrow: PropTypes.shape({}),
  }).isRequired,
  onEnded: PropTypes.func.isRequired,
};

export default Video;
